import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'

import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { platformFeedbackQuery } from '@/features/feedback/api'
import { FeedbackEntryRow } from '@/features/feedback/entry-row'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'

/** What restaurant users want fixed or added in Romi (I3); every platform user reads it. */
export function PlatformFeedbackPage() {
  const t = useT()
  const { page } = useSearch({ from: '/platform-auth/feedback' })
  const list = useQuery(platformFeedbackQuery(page))
  const link = (page: number) => ({ to: '/feedback', search: { page } }) as const
  useClampedPage(page, list.data, link)

  return (
    <Page width="lg" title={t('feedback.platform.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('feedback.platform.intro')}</p>
      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('feedback.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((entry) => (
              <li key={entry.id}>
                <FeedbackEntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('feedback.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </Page>
  )
}
